import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAuthentication,
  useEditAllocation,
  useMarkTaskAsCompleted,
} from "@planzone/view-models";

import { Modal } from "@common/modal";
import { Button } from "@common/button";
import { Checkbox } from "@common/checkbox";
import { Props } from "./types";
import { Form } from "./form";
import styles from "./styles.scss";
import { allocation } from "@planzone/dto";

function TaskCompletion({ cancel, task, project }: Props) {
  const { t } = useTranslation();
  const { account } = useAuthentication();

  const now = new Date();
  const [completionDate, setCompletionDate] = useState(
    new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
    ).toISOString(),
  );

  const [markAllocationComplete, setMarkAllocationComplete] = useState(false);
  const [markTaskComplete, setMarkTaskComplete] = useState(false);

  const userAllocation = task?.allocations?.find(
    (alloc) => alloc?.resource?.uuid === account?.resource?.uuid,
  );

  const hasMultipleAllocations = task?.allocations?.length > 1;

  const { isRequestPending, markTaskAsCompleted } =
    useMarkTaskAsCompleted(task);

  const { editAllocation } = useEditAllocation({ ...allocation, uuid: "" });

  const shouldShowFormDirectly =
    !hasMultipleAllocations || (!userAllocation && hasMultipleAllocations);
  const handleConfirm = () => {
    if (!userAllocation || !hasMultipleAllocations || markTaskComplete) {
      markTaskAsCompleted({
        task,
        project,
        completionDate,
      });
    }

    if (markAllocationComplete && userAllocation) {
      editAllocation({
        allocation: userAllocation,
        nextAllocation: {
          ...userAllocation,
          percentComplete: 100,
        },
      });
    }

    cancel();
  };

  return (
    <Modal
      header={t("taskCompletion.title")}
      headerClassName={styles.header}
      close={cancel}
    >
      <div className={styles.main}>
        {!shouldShowFormDirectly && (
          <h3 className={styles.title}>{t("taskCompletion.subTitle")}</h3>
        )}

        {!shouldShowFormDirectly &&
          userAllocation &&
          hasMultipleAllocations && (
            <>
              <div
                className={styles.section}
                onClick={() => setMarkAllocationComplete((prev) => !prev)}
              >
                <Checkbox isChecked={markAllocationComplete} />
                <p className={styles.label}>
                  {t("taskCompletion.markOnlyMyAllocation")}
                </p>
              </div>

              <div
                className={styles.section}
                onClick={() => setMarkTaskComplete((prev) => !prev)}
              >
                <Checkbox isChecked={markTaskComplete} />
                <p className={styles.label}>
                  {t("taskCompletion.markEntireTaskCompleted")}
                </p>
              </div>
            </>
          )}

        {(shouldShowFormDirectly || markTaskComplete) && (
          <div className={styles.form}>
            <p className={styles.label}>{t("taskCompletion.completionDate")}</p>
            <Form
              date={completionDate}
              taskEndDate={task?.endDate}
              editDate={setCompletionDate}
            />
          </div>
        )}

        <div className={styles.buttons}>
          <Button type="cancel" press={cancel}>
            {t("taskCompletion.cancel")}
          </Button>
          <Button
            className={styles.confirmButton}
            status={isRequestPending ? "pending" : "none"}
            isDisabled={isRequestPending}
            press={handleConfirm}
          >
            {t("taskCompletion.submit")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export { TaskCompletion };
